import React, { useContext, useEffect, useState } from 'react';
import moment from 'moment';
import { makeStyles } from '@material-ui/core/styles';
import Table from '@material-ui/core/Table';
import TableBody from '@material-ui/core/TableBody';
import TableCell from '@material-ui/core/TableCell';
import TableContainer from '@material-ui/core/TableContainer';
import TableHead from '@material-ui/core/TableHead';
import TableRow from '@material-ui/core/TableRow';
import Container from '@material-ui/core/Container';
import Checkbox from '@material-ui/core/Checkbox';
import RefreshIcon from '@mui/icons-material/Refresh';
import Paper from '@material-ui/core/Paper';
import Link from '@material-ui/core/Link';
import Box from '@material-ui/core/Box';
import Button from '@material-ui/core/Button';
import Error from '../../components/Error/Error';
import Loading from '../../components/Loading/Loading';
import { Context as StickyProductsContext } from '../../context/StickyProductsContext';
import { Context as UserContext } from '../../context/UserContext';
import { getDollarString } from '../../utils/helpers';
import axios from 'axios';
import { Typography } from '@material-ui/core';
import TableSortLabel from '@material-ui/core/TableSortLabel';

// --- Sorting Helper Functions ---
function descendingComparator(a, b, orderBy) {
  let valA = a[orderBy];
  let valB = b[orderBy];

  // Handle nested date object for 'updated_at'
  if (orderBy === 'updated_at' && valA && valB) {
    valA = valA.date;
    valB = valB.date;
  }

  // Handle 'active' which is '1' or '0' string
  if (orderBy === 'active') {
    valA = valA === '1';
    valB = valB === '1';
  }

  if (valB < valA) {
    return -1;
  }
  if (valB > valA) {
    return 1;
  }
  return 0;
}

function getComparator(order, orderBy) {
  return order === 'desc'
    ? (a, b) => descendingComparator(a, b, orderBy)
    : (a, b) => -descendingComparator(a, b, orderBy);
}

function stableSort(array, comparator) {
  const stabilizedThis = array.map((el, index) => [el, index]);
  stabilizedThis.sort((a, b) => {
    const order = comparator(a[0], b[0]);
    if (order !== 0) return order;
    return a[1] - b[1];
  });
  return stabilizedThis.map((el) => el[0]);
}

// --- Column Definitions ---
const headCells = [
  { id: 'updated_at', numeric: false, disablePadding: false, label: 'Updated at', sortable: true },
  { id: 'name', numeric: false, disablePadding: false, label: 'Name', sortable: true },
  { id: 'active', numeric: false, disablePadding: false, label: 'Active', sortable: true },
  { id: 'status', numeric: false, disablePadding: false, label: 'Status', sortable: false },
  { id: 'price', numeric: true, disablePadding: false, label: 'Price', sortable: false },
];

// --- Enhanced Table Head Component ---
function EnhancedTableHead(props) {
  const { classes, order, orderBy, onRequestSort } = props;
  const createSortHandler = (property) => (event) => {
    onRequestSort(event, property);
  };

  return (
    <TableHead>
      <TableRow>
        {headCells.map((headCell) => (
          <TableCell
            key={headCell.id}
            align={headCell.numeric ? 'right' : 'left'}
            padding={headCell.disablePadding ? 'none' : 'normal'}
            sortDirection={orderBy === headCell.id ? order : false}
          >
            {headCell.sortable ? (
              <TableSortLabel
                active={orderBy === headCell.id}
                direction={orderBy === headCell.id ? order : 'asc'}
                onClick={createSortHandler(headCell.id)}
              >
                {headCell.label}
                {orderBy === headCell.id ? (
                  <span className={classes.visuallyHidden}>
                    {order === 'desc' ? 'sorted descending' : 'sorted ascending'}
                  </span>
                ) : null}
              </TableSortLabel>
            ) : (
              headCell.label
            )}
          </TableCell>
        ))}
      </TableRow>
    </TableHead>
  );
}

const useStyles = makeStyles((theme) => ({
  table: {
    minWidth: 750,
  },
  paper: {
    width: '100%',
    marginBottom: theme.spacing(2),
    marginTop: theme.spacing(2),
  },
  title: {
    marginTop: theme.spacing(4),
  },
  topButtons: {
    display: 'flex',
    justifyContent: 'flex-end',
    marginTop: theme.spacing(4),
  },
  modalButtons: {
    display: 'flex',
    justifyContent: 'flex-end',
    marginTop: theme.spacing(3),
  },
  visuallyHidden: {
    border: 0,
    clip: 'rect(0 0 0 0)',
    height: 1,
    margin: -1,
    overflow: 'hidden',
    padding: 0,
    position: 'absolute',
    top: 20,
    width: 1,
  },
}));

export default function StickyProducts({ onRender }) {
  const classes = useStyles();
  const [refreshing, setRefreshing] = useState(false);
  const [refreshError, setRefreshError] = useState('');
  const [order, setOrder] = useState('desc');
  const [orderBy, setOrderBy] = useState('updated_at');

  const {
    state: { user },
  } = useContext(UserContext);

  const {
    state: { stickyProducts, loading, error },
    loadStickyProducts,
  } = useContext(StickyProductsContext);

  useEffect(() => {
    if (user) {
      loadStickyProducts();
    }

    onRender('Sticky products');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  const handleRequestSort = (event, property) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
  };

  async function handleRefresh() {
    try {
      setRefreshing(true);
      setRefreshError('');
      await axios.get('https://manna.com/api/sticky/getProducts');
      setRefreshing(false);
      loadStickyProducts();
    } catch (error) {
      setRefreshing(false);
      setRefreshError('Error happened while syncing. Please try again');
    }
  }

  if (error) {
    return <Error error={error} />;
  }

  if (loading || refreshing) {
    return <Loading />;
  }

  return (
    <>
      <Container className={classes.container}>
        <Box className={classes.topButtons}>
          <Box
            style={{
              display: 'flex',
              flexDirection: 'column',
            }}
          >
            <Button
              style={{
                alignSelf: 'flex-end',
                marginBottom: '10px',
              }}
              variant='contained'
              color='primary'
              startIcon={<RefreshIcon />}
              onClick={() => handleRefresh()}
            >
              Sync products
            </Button>
            {refreshError && (
              <Typography
                style={{
                  color: 'red',
                }}
                variant='caption'
                gutterBottom
              >
                {refreshError}
              </Typography>
            )}
          </Box>
        </Box>
        <TableContainer component={Paper} className={classes.paper}>
          <Table className={classes.table}>
            <EnhancedTableHead
              classes={classes}
              order={order}
              orderBy={orderBy}
              onRequestSort={handleRequestSort}
            />
            <TableBody>
              {stableSort(stickyProducts, getComparator(order, orderBy)).map((row) => (
                <TableRow key={row._id} hover>
                  <TableCell>{row.updated_at && moment(row.updated_at.date).format('lll')}</TableCell>
                  <TableCell>
                    <Link
                      variant='body2'
                      target='_blank'
                      href={`https://manna.sticky.io/admin/products/products.php?product_id=${row.id}`}
                    >
                      {`[${row.id}] - ${row.name}`}
                    </Link>
                  </TableCell>
                  <TableCell>
                    <Checkbox checked={row.active === '1'} color='primary' readOnly />
                  </TableCell>
                  <TableCell>{row.status}</TableCell>
                  <TableCell align='right'>{getDollarString(row.sale_price || row.price)}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Container>
    </>
  );
}
